import React, { useState, useMemo } from 'react';
import Skeleton from './Skeleton';
import { formatNLabel } from '../utils/deploymentStats';
import './StatisticalSummary.css';

export default function StatisticalSummary({ enrichedDeployment, responses = [], onSelectionRuleChange, loading = false }) {
  const [dismissedLowConf, setDismissedLowConf] = useState(false);

  // All hooks MUST be called before any conditional return (Rules of Hooks)

  // Low-confidence: count distinct recipient_ids where any row has confidence_weight < 0.65
  const { lowConfCount, totalRecipients } = useMemo(() => {
    if (loading || !responses.length) return { lowConfCount: 0, totalRecipients: 0 };
    const all = new Set(), flagged = new Set();
    responses.forEach(r => {
      if (r.recipient_id) {
        all.add(r.recipient_id);
        if (r.confidence_weight != null && r.confidence_weight < 0.65) flagged.add(r.recipient_id);
      }
    });
    return { lowConfCount: flagged.size, totalRecipients: all.size };
  }, [responses, loading]);

  // Repeat submitters: distinct recipients with 2+ completed sessions
  const { repeatCount, extraSessions } = useMemo(() => {
    if (loading || !responses.length) return { repeatCount: 0, extraSessions: 0 };
    const map = {};
    responses.forEach(r => {
      if (r.recipient_id && r.completion_status === 'completed' && r.session_id) {
        if (!map[r.recipient_id]) map[r.recipient_id] = new Set();
        map[r.recipient_id].add(r.session_id);
      }
    });
    const repeats = Object.values(map).filter(s => s.size > 1);
    return { repeatCount: repeats.length, extraSessions: repeats.reduce((a, s) => a + s.size - 1, 0) };
  }, [responses, loading]);

  // Conditional returns AFTER all hooks
  if (loading) {
    return (
      <div className="stat-summary" aria-busy="true">
        <div className="stat-summary-title">Statistical Summary</div>
        <Skeleton height="14px" count={7} />
      </div>
    );
  }

  if (!enrichedDeployment) return null;
  const d = enrichedDeployment;
  const pct = (n, total) => total > 0 ? ` (${Math.round(100 * n / total)}%)` : '';

  const receivedDisplay = d.received_count === null ? '—' : d.received_count;
  const optedOutDisplay = `${d.opted_out_count ?? 0}${pct(d.opted_out_count ?? 0, d.recipients_count ?? 0)}`;
  const closed = d.is_open === false;

  return (
    <div className="stat-summary">
      <div className="stat-status-row">
        {d.survey_status && (
          <span className="stat-lifecycle" title="Survey lifecycle state">{d.survey_status}</span>
        )}
        <span className={closed ? 'stat-openclosed stat-openclosed-closed' : 'stat-openclosed stat-openclosed-open'}
          title={closed ? 'Deployment closed — showing final values as history' : 'Deployment open'}>
          {closed ? 'Closed' : 'Open'}
        </span>
        {d.is_anonymous && <span className="stat-anon" title="Anonymous link deployment — n is completed/started">anonymous</span>}
      </div>
      <div className="stat-summary-grid">
        <div className="stat-cell">
          <span className="stat-label">Sent</span>
          <span className="stat-value">{d.sent_count ?? '—'}</span>
        </div>
        <div className="stat-cell">
          <span className="stat-label">Received</span>
          <span className="stat-value" title={d.received_count === null ? 'Channel does not provide delivery confirmation' : ''}>
            {receivedDisplay}
          </span>
        </div>
        <div className="stat-cell">
          <span className="stat-label">Opted out</span>
          <span className="stat-value">{optedOutDisplay}</span>
        </div>
        <div className="stat-cell">
          <span className="stat-label">Started</span>
          <span className="stat-value">{d.started_count ?? 0}</span>
        </div>
        <div className="stat-cell">
          <span className="stat-label">Completed</span>
          <span className="stat-value">{d.completed_count ?? 0}</span>
        </div>
        <div className="stat-cell">
          <span className="stat-label">Abandoned</span>
          <span className="stat-value">{d.abandoned_count ?? 0}</span>
        </div>
        <div className="stat-cell stat-nstar">
          <span className="stat-label">n =</span>
          <span className="stat-value" title={d.is_anonymous ? 'Anonymous: completed / started' : 'Responding / Sent (y / x)'}>
            {formatNLabel(d)}
          </span>
        </div>
      </div>

      {repeatCount > 0 && (
        <div className="stat-repeat">
          Repeat submitters: {repeatCount} recipients, {extraSessions} duplicate session{extraSessions !== 1 ? 's' : ''}
          <br />
          <label>Use: <select onChange={e => onSelectionRuleChange?.(e.target.value)} defaultValue="latest">
            <option value="latest">Latest completed</option>
            <option value="earliest">Earliest completed</option>
            <option value="all">All completed</option>
          </select></label>
        </div>
      )}

      {lowConfCount > 0 && (
        <div className="stat-lowconf">
          Low-confidence: {lowConfCount} of {totalRecipients} ({Math.round(100 * lowConfCount / totalRecipients)}%)
          {!dismissedLowConf && (
            <div className="stat-lowconf-notice">
              {lowConfCount} of {totalRecipients} responses have at least one low-confidence answer (confidence &lt; 0.65). These may be unlikely to be valid.
              <button onClick={() => setDismissedLowConf(true)} className="stat-dismiss">×</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
